import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAGIC_WORD = "CREATE";
const PATH_TO_LOCATION = {
  "/shop": "shop",
  "/custom-design": "custom",
  "/custom": "custom",
  "/bespoke": "bespoke",
  "/moto-club": "moto-club",
  "/club": "moto-club",
  "/collections": "collections",
  "/about": "about",
};

/**
 * Hidden codebreaker. Runs site-wide once mounted in App.js.
 * - Watches the route. When the user lands on a "secret" page, calls
 *   /codebreaker/collect to reveal that page's letter for their personal code.
 * - Listens for the magic word being typed on any page. Typing it opens the
 *   decoder modal where the player enters their 6-letter guess.
 * - On correct guess (logged-in): mints a single-use MOTOCODE-XXXXXX 25% off.
 * - **Guests**: typing the magic word triggers an email-capture popup. We
 *   email them a single-use 25% off code (MOTOFOUND-XXXXXX) and add them to
 *   the newsletter so the lead is captured even if they don't shop today.
 */
export default function Codebreaker() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [guess, setGuess] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  // Guest-only state
  const [guestEmail, setGuestEmail] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestErr, setGuestErr] = useState("");
  const [guestResult, setGuestResult] = useState(null);
  const [guestBusy, setGuestBusy] = useState(false);

  const keyBuf = useRef("");

  // Load progress once the user is signed in.
  useEffect(() => {
    if (!user) { setState(null); return; }
    client.get("/codebreaker/state").then((r) => setState(r.data)).catch(() => {});
  }, [user]);

  // Collect fragments when the user lands on a secret page.
  useEffect(() => {
    if (!user) return;
    const loc = PATH_TO_LOCATION[pathname];
    if (!loc) return;
    if (state && state.fragments_collected?.includes(loc)) return;
    client.post("/codebreaker/collect", { location: loc }).then((r) => {
      setState(r.data);
      if (r.data?.just_revealed) {
        setToast({
          letter: r.data.just_revealed.letter,
          position: r.data.just_revealed.position,
          total: r.data.fragments_total,
        });
        setTimeout(() => setToast(null), 5500);
      }
    }).catch(() => {});
  }, [pathname, user, state]);

  // Magic-word keystroke listener (desktop) + custom event listener (mobile triple-tap).
  // Works for guests too — opens the guest-claim popup instead of the decoder.
  useEffect(() => {
    const handler = (e) => {
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
      const k = (e.key || "").toUpperCase();
      if (!/^[A-Z]$/.test(k)) return;
      keyBuf.current = (keyBuf.current + k).slice(-MAGIC_WORD.length);
      if (keyBuf.current === MAGIC_WORD) {
        setOpen(true);
        keyBuf.current = "";
      }
    };
    const openHandler = () => setOpen(true);
    window.addEventListener("keydown", handler);
    window.addEventListener("motowear:open-codebreaker", openHandler);
    return () => {
      window.removeEventListener("keydown", handler);
      window.removeEventListener("motowear:open-codebreaker", openHandler);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (guess.length === 0) return;
    setBusy(true); setResult(null);
    try {
      const r = await client.post("/codebreaker/submit", { guess });
      setResult(r.data);
      if (r.data.correct) {
        // Refresh state so the badge updates
        const s = await client.get("/codebreaker/state");
        setState(s.data);
      }
    } catch (err) {
      setResult({ correct: false, reason: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const submitGuest = async (e) => {
    e.preventDefault();
    setGuestErr(""); setGuestResult(null);
    const email = guestEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setGuestErr("Pop in a valid email so we can send the code over.");
      return;
    }
    setGuestBusy(true);
    try {
      const r = await client.post("/codebreaker/guest-claim", { email, name: guestName.trim() });
      setGuestResult(r.data);
    } catch (err) {
      setGuestErr(err.response?.data?.detail || err.message || "Couldn't claim — try again.");
    } finally {
      setGuestBusy(false);
    }
  };

  const closeModal = () => {
    setOpen(false); setResult(null); setGuess("");
    setGuestErr(""); setGuestResult(null);
  };

  // Mask unrevealed letters with a dot. Build a length-6 string from state.
  const masked = (() => {
    const out = Array(state?.fragments_total || 6).fill("·");
    (state?.revealed_letters || []).forEach((r) => { out[r.position - 1] = r.letter; });
    return out;
  })();

  return (
    <>
      {/* Tiny floating "fragment collected" toast — logged-in only */}
      {toast && user && (
        <div
          className="fixed top-24 left-1/2 -translate-x-1/2 z-50 bg-black text-white px-5 py-3 border border-[#FF3B30] shadow-2xl"
          data-testid="codebreaker-toast"
        >
          <div className="label-eyebrow text-[#FF3B30]">Fragment found</div>
          <div className="mt-1 text-sm">
            Letter <span className="font-display text-xl moto-accent">{toast.letter}</span> revealed
            <span className="text-white/40 ml-2">({toast.position}/{toast.total})</span>
          </div>
          <div className="mt-1 text-[10px] text-white/40 uppercase tracking-widest">Type the magic word to decode</div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur" data-testid="codebreaker-modal">
          <div className="bg-black border border-[#FF3B30]/60 max-w-md w-[92%] p-8 relative">
            <button
              type="button"
              onClick={closeModal}
              className="absolute top-3 right-3 text-white/60 hover:text-white text-xs label-eyebrow"
              data-testid="codebreaker-close"
            >
              Close ✕
            </button>

            {/* GUEST flow: email-gated claim */}
            {!user && (
              <div data-testid="codebreaker-guest-gate">
                <div className="label-eyebrow text-[#FF3B30]">You found it</div>
                <h2 className="font-display text-3xl tracking-tight mt-2 text-white">Magic word, claimed.</h2>
                <p className="text-sm text-white/60 mt-2">
                  Drop your email — we'll send your single-use <strong className="text-white">25% off</strong> code straight to your inbox and log your name on the leaderboard.
                </p>

                {!guestResult ? (
                  <form onSubmit={submitGuest} className="mt-6 space-y-3" data-testid="codebreaker-guest-form">
                    <input
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Your name (optional)"
                      className="w-full bg-transparent border-b border-white/30 text-white py-3 focus:outline-none focus:border-[#FF3B30]"
                      data-testid="codebreaker-guest-name"
                    />
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      placeholder="Your email"
                      autoFocus
                      className="w-full bg-transparent border-b border-white/30 text-white py-3 focus:outline-none focus:border-[#FF3B30]"
                      data-testid="codebreaker-guest-email"
                      required
                    />
                    {guestErr && (
                      <div className="text-sm text-[#FF3B30]" data-testid="codebreaker-guest-error">{guestErr}</div>
                    )}
                    <button
                      type="submit"
                      disabled={guestBusy}
                      className="w-full bg-[#FF3B30] text-white py-3 label-eyebrow disabled:opacity-40"
                      data-testid="codebreaker-guest-submit"
                    >
                      {guestBusy ? "Claiming…" : "Email me my 25% off"}
                    </button>
                    <div className="text-[10px] text-white/40 leading-relaxed">
                      By claiming you agree to receive occasional drops + offers from MotoWear. Unsubscribe at any time.
                    </div>
                  </form>
                ) : (
                  <div className="mt-6 space-y-3 text-center" data-testid="codebreaker-guest-success">
                    <div className="label-eyebrow text-[#FF3B30]">Code on its way</div>
                    <div className="font-mono text-lg text-white mt-2">{guestResult.promo_code}</div>
                    <div className="text-xs text-white/50">25% off — also sent to your inbox.</div>
                    <a
                      href="/register"
                      className="inline-block mt-3 bg-white text-black px-5 py-2.5 label-eyebrow hover:bg-[#FF3B30] hover:text-white transition-colors"
                      data-testid="codebreaker-guest-register"
                    >
                      Make an account to unlock the full hunt
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* LOGGED-IN flow: full decoder */}
            {user && (
              <>
                <div className="label-eyebrow text-[#FF3B30]">Decoder</div>
                <h2 className="font-display text-3xl tracking-tight mt-2 text-white">Crack the code.</h2>
                <p className="text-sm text-white/60 mt-2">Six letters. One reward. Visit six hidden pages on this site to reveal them.</p>

                <div className="mt-6 flex justify-center gap-2 md:gap-3 flex-wrap" data-testid="codebreaker-progress">
                  {masked.map((c, i) => (
                    <div
                      key={i}
                      className={`w-10 md:w-12 h-12 md:h-14 flex items-center justify-center font-display text-2xl md:text-3xl ${c === "·" ? "border border-white/20 text-white/30" : "border border-[#FF3B30] text-white bg-[#FF3B30]/10"}`}
                      data-testid={`codebreaker-slot-${i}`}
                    >
                      {c}
                    </div>
                  ))}
                </div>
                <div className="text-center text-xs text-white/40 mt-2">
                  {state?.fragments_collected_count || 0} / {state?.fragments_total || 6} fragments collected
                </div>

                {state?.solved ? (
                  <div className="mt-8 text-center" data-testid="codebreaker-solved">
                    <div className="label-eyebrow text-[#FF3B30]">Already cracked</div>
                    <div className="font-mono text-xl text-white mt-2">{state.promo_code}</div>
                    <div className="text-xs text-white/50 mt-2">25% off — check your email for full details.</div>
                  </div>
                ) : (
                  <form onSubmit={submit} className="mt-8 space-y-4" data-testid="codebreaker-form">
                    <input
                      value={guess}
                      onChange={(e) => setGuess(e.target.value.toUpperCase().slice(0, 6))}
                      placeholder="Enter the 6-letter code"
                      className="w-full bg-transparent border-b border-white/30 text-center text-white text-2xl tracking-[0.4em] font-mono py-3 focus:outline-none focus:border-[#FF3B30]"
                      data-testid="codebreaker-input"
                      autoFocus
                    />
                    <button
                      type="submit"
                      disabled={busy || guess.length !== 6}
                      className="w-full bg-[#FF3B30] text-white py-3 label-eyebrow disabled:opacity-40"
                      data-testid="codebreaker-submit"
                    >
                      {busy ? "Checking…" : "Decode"}
                    </button>
                    {result && !result.correct && (
                      <div className="text-sm text-white/70 text-center" data-testid="codebreaker-wrong">
                        {result.reason || "Wrong code. Keep hunting."}
                      </div>
                    )}
                    {result?.correct && result.promo_code && (
                      <div className="text-center mt-4" data-testid="codebreaker-win">
                        <div className="label-eyebrow text-[#FF3B30]">Code cracked</div>
                        <div className="font-mono text-lg text-white mt-2">{result.promo_code}</div>
                        <div className="text-xs text-white/50 mt-2">25% off your next order. Sent to your email.</div>
                      </div>
                    )}
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
